import { pushToDataLayer } from './gtm';
import { withExpVariant } from './kamExpVariant';
import { isQAAnalyticsEnvironment } from './lendAPIUtils';
import {
  GATrackingConfig,
  FullAppSubmittedEventConfig,
  LeadSubmittedEventConfig,
} from '../lendAPIConfig.types';

export interface GAPageviewEvent {
  event: 'pageview';
  detailed_page_name: string;
  site_section: 'trident finance';
  funnel: string;
}

export interface GAApplicationSubmittedEvent {
  event: 'application_submitted';
  action_type: 'Boat';
  transaction_id?: string;
  value?: string;
  currency: 'USD';
  funnel?: string;
}

export interface GAFullAppSubmittedEvent {
  event: 'application_submitted';
  transaction_id: string;
  funnel: string;
  ecommerce: {
    currencyCode: 'USD';
    purchase: {
      actionField: {
        id: string;
        affiliation: string;
        revenue?: number;
      };
      products: Array<{
        name: string;
        id: string;
        price?: number;
        category: string;
      }>;
    };
  };
  // Raw approval signals from the LendAPI application response (Combined funnel only).
  approval_odds?: number;
  approval_outcome?: string;
}

export interface GAUnqualifiedLeadEvent {
  event: 'unqualified_lead';
  currency: 'USD';
  value?: string;
  transaction_id?: string;
  disqualified_lead_reason: 'could not verify identity';
  funnel?: string;
}

export interface GADisqualifyLeadEvent {
  event: 'disqualify_lead';
  currency: 'USD';
  value?: string;
  transaction_id?: string;
  disqualified_lead_reason: 'did not meet requirements';
  funnel?: string;
}

export type GAResultEvent =
  | GAApplicationSubmittedEvent
  | GAUnqualifiedLeadEvent
  | GADisqualifyLeadEvent;

export interface ResultScenarioConfig {
  enabled: boolean;
  tabName: string;
}

export interface ResultsConfig {
  qualified: ResultScenarioConfig;
  unqualified: ResultScenarioConfig;
  declined: ResultScenarioConfig;
}

export interface ResultsPagePayload {
  applicationId?: string;
  purchasingPower?: number;
}

export const normalizeTabName = (tabName: string): string => {
  if (!tabName) return '';

  return tabName
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/-/g, ' - ')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

export const isTabMatch = (tabName: string, triggerTab: string | string[]): boolean => {
  const normalizedTab = normalizeTabName(tabName);
  if (Array.isArray(triggerTab)) {
    return triggerTab.some((t) => normalizedTab === normalizeTabName(t));
  }
  return normalizedTab === normalizeTabName(triggerTab);
};

export const getFunnelValueForPageview = (productType: string): string => {
  switch (productType) {
    case 'fullApp':
      return 'lendapi full-app';
    case 'combined':
      return 'lendapi combined';
    default:
      return `lendapi ${productType}`;
  }
};

export const getFunnelValueForSubmitted = (productType: string): string => {
  switch (productType) {
    case 'fullApp':
      return 'lendapi full-app';
    case 'combined':
      return 'lendapi combined | full-app';
    default:
      return `lendapi ${productType}`;
  }
};

export const getFunnelValueForResultsPage = (productType: string): string => {
  switch (productType) {
    case 'combined':
      return 'lendapi combined | pre-qual';
    default:
      return getFunnelValueForPageview(productType);
  }
};

export const getPageviewEventData = (tabName: string, funnel: string): GAPageviewEvent => {
  const normalizedTabName = normalizeTabName(tabName);

  return {
    event: 'pageview',
    detailed_page_name: normalizedTabName,
    site_section: 'trident finance',
    funnel,
  };
};

export const getFullAppSubmittedEventData = (
  config: FullAppSubmittedEventConfig,
  tabName: string,
  funnel: string,
  payload?: any
): GAFullAppSubmittedEvent | null => {
  if (!config?.enabled || normalizeTabName(tabName) !== normalizeTabName(config.tabName))
    return null;

  const transactionId = payload?.session || '';

  return {
    event: 'application_submitted',
    transaction_id: transactionId,
    funnel,
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: {
          id: transactionId,
          affiliation: '',
          revenue: payload?.purchase_price || payload?.boats_purchasing_power || undefined,
        },
        products: [
          {
            name: 'boat',
            id: 'boat',
            price: payload?.purchase_price || payload?.boats_purchasing_power || undefined,
            category: payload?.loan_type?.toLowerCase() || 'new',
          },
        ],
      },
    },
    ...(typeof payload?.approval_odds === 'number' && { approval_odds: payload.approval_odds }),
    ...(typeof payload?.approval_outcome === 'string' &&
      payload.approval_outcome.length > 0 && { approval_outcome: payload.approval_outcome }),
  };
};

export const getResultsPageEventData = (
  tabName: string,
  config: ResultsConfig,
  payload?: ResultsPagePayload | null,
  funnel?: string
): GAResultEvent | null => {
  if (!tabName || !config) return null;

  const safePayload = payload || {};

  if (config.qualified.enabled && tabName === config.qualified.tabName) {
    const value = safePayload.purchasingPower
      ? (parseFloat(safePayload.purchasingPower.toString()) || 0).toFixed(2)
      : undefined;

    return {
      event: 'application_submitted',
      action_type: 'Boat',
      transaction_id: safePayload.applicationId
        ? `lendapi-${safePayload.applicationId}`
        : undefined,
      value,
      currency: 'USD',
      ...(funnel && { funnel }),
    };
  }

  if (config.unqualified.enabled && tabName === config.unqualified.tabName) {
    const value = safePayload.purchasingPower
      ? (parseFloat(safePayload.purchasingPower.toString()) || 0).toFixed(2)
      : undefined;

    return {
      event: 'unqualified_lead',
      currency: 'USD',
      value,
      transaction_id: safePayload.applicationId
        ? `lendapi-${safePayload.applicationId}`
        : undefined,
      disqualified_lead_reason: 'could not verify identity',
      ...(funnel && { funnel }),
    };
  }

  if (config.declined.enabled && tabName === config.declined.tabName) {
    const value = safePayload.purchasingPower
      ? (parseFloat(safePayload.purchasingPower.toString()) || 0).toFixed(2)
      : undefined;

    return {
      event: 'disqualify_lead',
      currency: 'USD',
      value,
      transaction_id: safePayload.applicationId
        ? `lendapi-${safePayload.applicationId}`
        : undefined,
      disqualified_lead_reason: 'did not meet requirements',
      ...(funnel && { funnel }),
    };
  }

  return null;
};

export interface GALeadSubmittedEvent {
  event: 'lead_submitted';
  action_type: string;
  transaction_id?: string;
  value?: string;
  currency: 'USD';
  // Derived from the normalized iframe tab name (e.g. "approval odds - excellent").
  prequal_result: string;
  funnel?: string;
  // Raw approval signals from the LendAPI application response (Combined funnel only).
  // Distinct from prequal_result above: prequal_result is computed client-side from the tab name,
  // while approval_outcome is the underwriting label LendAPI emits with the application.
  approval_odds?: number;
  approval_outcome?: string;
}

export const getLeadSubmittedEventData = (
  tabName: string,
  config: LeadSubmittedEventConfig,
  payload?: any,
  funnel?: string
): GALeadSubmittedEvent | null => {
  if (!config?.enabled || !tabName) return null;

  if (!isTabMatch(tabName, config.triggerTabs)) return null;

  const safePayload = payload || {};
  const transactionId = safePayload.session ? `lendapi-${safePayload.session}` : undefined;
  const rawValue = safePayload.purchase_price || safePayload.boats_purchasing_power;
  const value = rawValue ? (parseFloat(rawValue.toString()) || 0).toFixed(2) : undefined;

  return {
    event: 'lead_submitted',
    action_type: safePayload.loan_purpose || 'Boat',
    transaction_id: transactionId,
    value,
    currency: 'USD',
    prequal_result: normalizeTabName(tabName),
    ...(funnel && { funnel }),
    ...(typeof safePayload.approval_odds === 'number' && {
      approval_odds: safePayload.approval_odds,
    }),
    ...(typeof safePayload.approval_outcome === 'string' &&
      safePayload.approval_outcome.length > 0 && {
        approval_outcome: safePayload.approval_outcome,
      }),
  };
};

let gaEnvironmentLogged = false;

export const handleGATracking = (
  tabName: string,
  gaConfig: GATrackingConfig,
  productType: string,
  payload?: any
): void => {
  if (!tabName || !gaConfig?.enabled) return;

  if (!gaEnvironmentLogged) {
    gaEnvironmentLogged = true;
    console.log(
      `[LendAPI ${productType}][GA][TRACKING] GA events environment: ${
        isQAAnalyticsEnvironment() ? 'QA' : 'Prod'
      }`
    );
  }

  const pageviewFunnel = getFunnelValueForPageview(productType);

  const pageviewEvent = getPageviewEventData(tabName, pageviewFunnel);
  pushToDataLayer(
    pageviewEvent.event,
    withExpVariant({
      detailed_page_name: pageviewEvent.detailed_page_name,
      site_section: pageviewEvent.site_section,
      funnel: pageviewEvent.funnel,
    })
  );
  console.log(
    `[LendAPI ${productType}][GA][TRACKING] Pageview event sent:`,
    pageviewEvent.detailed_page_name
  );

  if (gaConfig?.resultsPageEvents) {
    const resultsPayload: ResultsPagePayload = {
      applicationId: payload?.session,
      purchasingPower: payload?.boats_purchasing_power,
    };

    const resultsFunnel = getFunnelValueForResultsPage(productType);

    const resultEvent = getResultsPageEventData(
      tabName,
      gaConfig.resultsPageEvents,
      resultsPayload,
      resultsFunnel
    );

    if (resultEvent) {
      const { event, ...eventData } = resultEvent;
      pushToDataLayer(event, eventData);
      console.log(`[LendAPI ${productType}][GA][TRACKING] Result event sent:`, event);
    }
  }

  if (gaConfig?.leadSubmittedEvent) {
    const resultsFunnel = getFunnelValueForResultsPage(productType);
    const leadEvent = getLeadSubmittedEventData(
      tabName,
      gaConfig.leadSubmittedEvent,
      payload,
      resultsFunnel
    );
    if (leadEvent) {
      const { event, ...eventData } = leadEvent;
      pushToDataLayer(event, withExpVariant(eventData));
      console.log(
        `[LendAPI ${productType}][GA][TRACKING] lead_submitted event sent | prequal_result:`,
        eventData.prequal_result
      );
    }
  }

  if (gaConfig?.fullAppSubmittedEvent) {
    const submittedFunnel = getFunnelValueForSubmitted(productType);

    const submittedEvent = getFullAppSubmittedEventData(
      gaConfig.fullAppSubmittedEvent,
      tabName,
      submittedFunnel,
      payload
    );

    if (submittedEvent) {
      const { event, ...eventData } = submittedEvent;
      pushToDataLayer(event, withExpVariant(eventData));
      console.log(
        `[LendAPI ${productType}][GA][TRACKING] Full App - application_submitted event sent`,
        eventData
      );
    }
  }
};
